import * as RestClient from '../../utils/rest-service';
import Constants from '../../config/constants';
import BecScComponent from './bec_sc_component';

export const ACTION = {
  LIST: 'LIST_SECTION_COMPONENT',
  CREATE: 'CREATE_SECTION_COMPONENT',
  EDIT: 'EDIT_SECTION_COMPONENT',
  DELETE: 'DELETE_SECTION_COMPONENT',
  COMPUTE_SC: 'COMPUTE_SC',
  EDIT_SHADING_DEVICE: 'EDIT_SHADING_DEVICE',
};

export const addComponentToSection = (componentId, sectionId, area, SC) => {
  const formData = new FormData();
  formData.set('section_id', sectionId);
  formData.set('component_id', componentId);
  formData.set('area', area);
  formData.set('SC', SC);

  return {
    type: ACTION.CREATE,
    payload: RestClient.request({
      method: 'post',
      url: Constants.API_SECTION_COMPONENT,
      data: formData,
    }).catch(err => Promise.reject(err.response.data)),
  };
};

export const editComponentInSection = (id, area, SC) => {
  const formData = new FormData();
  formData.set('area', area);
  formData.set('SC', SC);

  return {
    type: ACTION.EDIT,
    payload: RestClient.request({
      method: 'patch',
      url: `${Constants.API_SECTION_COMPONENT}${id}/`,
      data: formData,
    }).catch(err => Promise.reject(err.response.data)),
  };
};

export const removeComponentFromSection = id => {
  return {
    type: ACTION.DELETE,
    payload: RestClient.request({
      method: 'delete',
      url: `${Constants.API_SECTION_COMPONENT}${id}/`,
    })
      .then(() => ({ id }))
      .catch(err => Promise.reject(err.response.data)),
  };
};

export const listComponentInSection = (pageSize, page, sorted, filtered, isPublic, sectionId) => {
  const sectionParams = sectionId === null ? { section_id__isnull: true } : { section_id: sectionId };
  return {
    type: ACTION.LIST,
    payload: RestClient.request({
      method: 'get',
      url: Constants.API_SECTION_COMPONENT,
      params: {
        search: filtered,
        limit: pageSize,
        offset: pageSize * page,
        ordering: sorted
          ? sorted.map(sort => sort.desc ? `-${sort.id}` : sort.id ).join(',')
          : null,
        is_public: isPublic,
        ...sectionParams,
      },
    }).catch(err => Promise.reject(err.response.data)),
  };
};

export const SDDirection = {
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
  FRONT: 'front',
};

const normSD = (window, device, type) => {
  const normalizeAngle = angle => angle % 360;
  const UPRIGHT_ANGLE = 90;

  switch (type) {
    case SDDirection.TOP:
    case SDDirection.FRONT:
      if (device.xAngle === 0) {
        return {
          ...device,
          size: {
            width: device.size.height,
            height: device.size.width,
          },
          azimuth: normalizeAngle(window.azimuth + UPRIGHT_ANGLE),
          angle: device.xAngle,
        };
      } else if (device.xAngle === 90) {
        return {
          ...device,
          azimuth: window.azimuth,
          angle: device.xAngle,
        };
      }
      return device;
    case SDDirection.RIGHT:
      return {
        ...device,
        origin: { x: device.origin.x, y: window.width, z: device.origin.z },
        azimuth: normalizeAngle(window.azimuth + UPRIGHT_ANGLE + (device.azimuth || 0)),
        angle: UPRIGHT_ANGLE,
      };
    case SDDirection.LEFT:
      return {
        ...device,
        azimuth: normalizeAngle(window.azimuth + UPRIGHT_ANGLE + (device.azimuth || 0)),
        angle: UPRIGHT_ANGLE,
      };
    default:
      return device;
  }
};

export const normSDs = (window, device, type) => {
  const DIGIT = 2;
  const radToDeg = rad => (rad * Math.PI) / 180;
  const have_to_split = device.xAngle > 0 && device.xAngle < 90;
  const devices = [];
  const angle = radToDeg(device.xAngle);
  if (SDDirection.TOP === type && have_to_split) {
    devices.push(
      normSD(
        window,
        {
          ...device,
          size: {
            width: device.size.width,
            height: +Math.fround(device.size.height * Math.cos(angle)).toFixed(DIGIT),
          },
          xAngle: 0,
        },
        SDDirection.TOP
      )
    );
    devices.push(
      normSD(
        window,
        {
          ...device,
          origin: {
            ...device.origin,
            x: +(device.origin.x + Math.fround(device.size.height * Math.cos(angle))).toFixed(DIGIT),
            z: +(device.origin.z - +Math.fround(device.size.height * Math.sin(angle))).toFixed(DIGIT)
          },
          size: {
            width: device.size.width,
            height: +Math.fround(device.size.height * Math.sin(angle)).toFixed(DIGIT),
          },
          azimuth: window.azimuth,
          xAngle: 90,
        },
        SDDirection.FRONT,
      )
    );
  } else if (SDDirection.FRONT === type && have_to_split) {
    devices.push(
      normSD(
        window,
        {
          ...device,
          origin: {
            ...device.origin,
            x: +Math.fround(device.origin.x - device.size.height * Math.cos(angle)).toFixed(DIGIT),
            z: +Math.fround(device.origin.z + device.size.height * Math.sin(angle)).toFixed(DIGIT),
          },
          size: {
            width: device.size.width,
            height: +Math.fround(device.size.height * Math.cos(angle)).toFixed(DIGIT),
          },
          xAngle: 0,
        },
        SDDirection.TOP
      )
    );
    devices.push(
      normSD(
        window,
        {
          ...device,
          origin: {
            ...device.origin,
            x: +(device.origin.x + Math.fround(device.size.height * Math.cos(angle))).toFixed(DIGIT),
          },
          size: {
            width: device.size.width,
            height: +Math.fround(device.size.height * Math.sin(angle)).toFixed(DIGIT),
          },
          azimuth: window.azimuth,
          xAngle: 90,
        },
        SDDirection.FRONT,
      )
    );
  }else {
    devices.push(normSD(window, device, type));
  }
  return devices;
};

export const computeSC = (sectionComponentId, azimuth, window, leftDevs, rightDevs, topDevs, frontDevs) => {
  const RIGHT_ANGLE = 90;

  // Support both rectangular and polygon window definitions
  const windowComponentJson = (() => {
    if (window && window.vertices && window.vertices.length >= 3) {
      return {
        origin: { x: 0, y: 0, z: 0 },
        azimuth,
        tilt: RIGHT_ANGLE,
        vertices: window.vertices.map(p => ({ x: parseFloat(p.x || 0), y: parseFloat(p.y || 0) })),
        enabled: true,
      };
    }
    return new BecScComponent(window.width, window.height, 0, 0, 0)
    .setTilt(RIGHT_ANGLE)
      .setAzimuth(azimuth)
      .print();
  })();

  const newWindow = {
    ...window,
    azimuth,
  };

  const devices = [].concat.apply(
    [],
    [
      ...leftDevs.map(item => normSDs(newWindow, item, SDDirection.LEFT)),
      ...rightDevs.map(item => normSDs(newWindow, item, SDDirection.RIGHT)),
      ...topDevs.map(item => normSDs(newWindow, item, SDDirection.TOP)),
      ...frontDevs.map(item => normSDs(newWindow, item, SDDirection.FRONT)),
    ],
  );

  const deviceComponents = devices.map(dev => {
    const {
      size: { width, height },
      origin: { x, y, z },
      angle,
      azimuth,
      vertices,
      xAngle,
    } = dev;

    const toNum = v => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const ox = x !== undefined ? toNum(x) : 0;
    const oy = y !== undefined ? toNum(y) : 0;
    const oz = z !== undefined ? toNum(z) : 0;
    const tilt = angle !== undefined ? angle : (xAngle !== undefined ? xAngle : 0);
    const az = azimuth !== undefined ? azimuth : 0;

    // Polygon shading device (keep all vertices)
    if (vertices && vertices.length >= 3) {
      return {
        origin: { x: ox, y: oy, z: oz },
        azimuth: az,
        tilt,
        vertices: vertices.map(p => ({
          x: toNum(p.x),
          y: toNum(p.y),
        })),
        enabled: true,
      };
    }

    // Rectangle shading device (legacy)
    return new BecScComponent(width, height, ox, oy, oz)
      .setTilt(tilt)
      .setAzimuth(az);
  });

  const json = {
    window: windowComponentJson,
    sheets: deviceComponents.map(component =>
      typeof component.print === 'function' ? component.print() : component
    ),
  };

  return {
    type: ACTION.COMPUTE_SC,
    payload: RestClient.request({
      method: 'post',
      url: `${Constants.API_SECTION_COMPONENT}${sectionComponentId}/calc_sc/`,
      data: json,
    })
      .catch(err => Promise.reject(err.response.data)),
  };
};

export const editWindowAndShadingDevices = (sectionId, area, SC, rawSC, structure) => {
  const json = {
    area,
    SC,
    shading_device_0: JSON.stringify(structure),
    shading_device_1: JSON.stringify(rawSC),
  };
  return {
    type: ACTION.EDIT_SHADING_DEVICE,
    payload: RestClient.request({
      method: 'patch',
      url: `${Constants.API_SECTION_COMPONENT}${sectionId}/`,
      data: json,
    }).catch(err => Promise.reject(err.response.data)),
  };
};



// WEBPACK FOOTER //
// ./src/redux/section/section_component_actions.js